import type {
  CarTileFbsInformation,
  Decision,
  SmallBatchApplication,
  SmallBatchLenderOffer,
  TradeInBreakdown,
} from '@/types/FinanceHelperTypes';

const getDecisionType = (
  decision: string,
  lenderName: string,
  carMonthlyPayment: number,
  totalFinanceAmount: number,
): Decision => {
  switch (decision.toLocaleLowerCase()) {
    case 'approved':
      return carMonthlyPayment === 0 ? 'notNeeded' : 'preApproved';

    case 'countered':
      return lenderName === 'Cash' && totalFinanceAmount === 0
        ? 'declined'
        : 'additionalDown';

    case 'declined':
      return 'declined';

    default:
      return 'noInfo';
  }
};

const getTradeInEquity = (tradeInBreakdown: TradeInBreakdown) => {
  /*
    if negativeEquity is nonzero, show negativeEquity
    if positiveEquity is >=0, show appliedPositiveEquity (i.e its possible to get a positive equity of $0)
  */
  const { appliedPositiveEquity, negativeEquity } = tradeInBreakdown;
  if (negativeEquity !== 0) return Math.abs(negativeEquity);

  return -appliedPositiveEquity;
};

/**
 * Formats data for use with the car tile's `fbsInformation` prop.
 */
export const carTileFinanceHelper = (
  basePrice: number,
  lenderOffer: SmallBatchLenderOffer,
  financeApplication: SmallBatchApplication,
): CarTileFbsInformation => {
  if (!lenderOffer) {
    return {
      decision: 'noInfo',
    };
  }

  const {
    terms,
    lenderName,
    offerStatus,
    taxesAndFees,
    conditionAmount,
    totalFinanceAmount,
  } = lenderOffer;

  const termLength = terms ? terms[0].length : undefined;
  const minApr = terms ? terms[0].minTermRate.apr : undefined;
  const maxApr = terms ? terms[0].maxTermRate.apr : undefined;
  const minMonthlyPayment = terms
    ? terms[0].minTermRate.monthlyPayment
    : undefined;
  const maxMonthlyPayment = terms
    ? terms[0].maxTermRate.monthlyPayment
    : undefined;

  const decision = getDecisionType(
    offerStatus,
    lenderName,
    maxMonthlyPayment || 0,
    totalFinanceAmount,
  );

  if (decision === 'declined' || decision === 'noInfo') return { decision };

  const { currentDownPayment, tradeInBreakdown } = financeApplication;

  const showTradeInEquity = !Object.values(tradeInBreakdown).every(
    (x) => x === null || x === 0,
  );

  const finalVehicleFinancePrice = showTradeInEquity
    ? basePrice +
      taxesAndFees -
      (currentDownPayment + conditionAmount) -
      (tradeInBreakdown.negativeEquity + tradeInBreakdown.appliedPositiveEquity)
    : basePrice + taxesAndFees - (currentDownPayment + conditionAmount);

  const values: CarTileFbsInformation = {
    decision,
    termLength,
    terms: terms ?? undefined,
    estimatedFees: taxesAndFees,
    downPayment: currentDownPayment,
    conditionAmount,
    minApr,
    maxApr,
    minMonthlyPayment,
    maxMonthlyPayment,
    finalVehicleFinancePrice,
  };

  if (showTradeInEquity) {
    values.tradeInEquityAmount = getTradeInEquity(tradeInBreakdown);
  }

  return values;
};
